/**
 * Plan 33 — Real Tipalti card for Settings > My-Kitchen.
 *
 * Activates the Plan 19 placeholder. Renders three states:
 *
 *   - **Not connected** — primary "Send onboarding invite" button emails
 *     the payee a Tipalti onboarding link. Also keeps the legacy "Request
 *     a payout change" CTA available so chef admins can still file a
 *     manual change request.
 *   - **Connected** — green status badge + connect timestamp + last
 *     rotation timestamp + a "Disconnect" button.
 *   - **Re-authorization required** — red status badge with a callout
 *     directing the chef admin to contact Ordo. Re-authorization is
 *     ORDO_ADMIN-initiated (the credential row's INVALID state is
 *     usually triggered by a Tipalti-side problem only ORDO_ADMIN can
 *     resolve).
 *
 * State derives from `GET /api/tipalti/payees/<orgId>/status`.
 * Setup calls `POST /api/tipalti/payees/<orgId>/invite`, which asks
 * Tipalti to email the payee their onboarding link.
 *
 * Loading state uses `@ordo/ui/skeleton` per the loading-state
 * convention.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { AlertTriangle, Banknote, CheckCircle2, Mail, Pencil } from 'lucide-react'
import { useState } from 'react'

import { type KitchenPayoutDestination, useKitchenPayoutsQuery } from '@/api/kitchen-settings'
import {
  type TipaltiStatus,
  type TipaltiStatusResponse,
  useDisconnectTipaltiMutation,
  useInviteTipaltiPayeeMutation,
  useTipaltiStatusQuery,
} from '@/api/tipalti'

import { KitchenChangeRequestModal } from './kitchen-change-request-modal'

export interface TipaltiCardProps {
  kitchenOrgId: string
}

export function TipaltiCard({ kitchenOrgId }: TipaltiCardProps) {
  const statusQuery = useTipaltiStatusQuery(kitchenOrgId)
  const payoutsQuery = useKitchenPayoutsQuery(kitchenOrgId)
  const [requestOpen, setRequestOpen] = useState(false)

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Banknote className="h-4 w-4" />
            Payouts
            <StatusBadge status={statusQuery.data?.status ?? null} />
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {statusQuery.data?.status === 'MANUAL_BILLING'
              ? "How Ordo pays you. Payouts run on your kitchen's payout schedule."
              : "How Ordo sends your AP payouts via Tipalti. Connect once during onboarding; payouts run automatically on your kitchen's payout schedule."}
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {statusQuery.isPending && !statusQuery.data ? (
            <CardSkeleton />
          ) : statusQuery.error ? (
            <p className="text-sm text-destructive">Couldn't load Tipalti status. {statusQuery.error.message}</p>
          ) : statusQuery.data?.status === 'CONNECTED' ? (
            <ConnectedView
              kitchenOrgId={kitchenOrgId}
              status={statusQuery.data}
              destination={payoutsQuery.data?.payoutDestination ?? null}
            />
          ) : statusQuery.data?.status === 'REAUTHORIZATION_REQUIRED' ? (
            <ReauthorizationRequiredView lastRotatedAt={statusQuery.data.lastRotatedAt} />
          ) : statusQuery.data?.status === 'MANUAL_BILLING' ? (
            <ManualBillingView destination={payoutsQuery.data?.payoutDestination ?? null} />
          ) : (
            <NotConnectedView kitchenOrgId={kitchenOrgId} destination={payoutsQuery.data?.payoutDestination ?? null} />
          )}

          {/* Manual change-request CTA — still useful as an escape hatch. */}
          <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => setRequestOpen(true)}>
            <Pencil className="mr-1 h-3 w-3" />
            Request a payout change
          </Button>
        </CardContent>
      </Card>

      {requestOpen ? (
        <KitchenChangeRequestModal
          open
          onOpenChange={setRequestOpen}
          kitchenOrgId={kitchenOrgId}
          field="payoutDestination"
          fieldLabel="Payout destination"
          currentValue={formatPayoutForChangeRequest(payoutsQuery.data?.payoutDestination ?? null)}
        />
      ) : null}
    </>
  )
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

function NotConnectedView({
  kitchenOrgId,
  destination,
}: {
  kitchenOrgId: string
  destination: KitchenPayoutDestination | null
}) {
  const inviteMutation = useInviteTipaltiPayeeMutation(kitchenOrgId)

  return (
    <>
      <div className="rounded-md border border-dashed bg-muted/30 p-4">
        <Badge variant="outline" className="text-xs">
          Not connected
        </Badge>
        <p className="mt-2 text-sm text-muted-foreground">
          Ordo hasn't received your payout info yet. Send yourself a Tipalti onboarding invite to enable automatic AP
          payouts on your kitchen's schedule.
        </p>
        {destination && destination.status !== 'NOT_CONFIGURED' ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Legacy provisioning saw: {destination.bankName ?? 'unknown bank'} •••• {destination.last4 ?? '----'}. This
            will be replaced when you connect Tipalti.
          </p>
        ) : null}
      </div>
      {inviteMutation.isSuccess ? (
        <p className="flex items-center gap-1.5 text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4" />
          Invitation sent — check your email to finish Tipalti setup.
        </p>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => inviteMutation.mutate(kitchenOrgId)}
            disabled={inviteMutation.isPending}>
            <Mail className="mr-1 h-3 w-3" />
            {inviteMutation.isPending ? 'Sending…' : 'Send onboarding invite'}
          </Button>
          {inviteMutation.error ? (
            <span className="text-xs text-destructive">{inviteMutation.error.message}</span>
          ) : null}
        </div>
      )}
    </>
  )
}

function ConnectedView({
  kitchenOrgId,
  status,
  destination,
}: {
  kitchenOrgId: string
  status: TipaltiStatusResponse
  destination: KitchenPayoutDestination | null
}) {
  const disconnectMutation = useDisconnectTipaltiMutation(kitchenOrgId)

  return (
    <div className="rounded-md border bg-card p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</span>
          <span className="flex items-center gap-1.5 text-sm font-medium">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            Connected via Tipalti
          </span>
          {destination?.bankName ? (
            <span className="text-xs text-muted-foreground">
              {destination.bankName} •••• {destination.last4 ?? '----'}
            </span>
          ) : null}
          {status.connectedAt ? (
            <span className="text-xs text-muted-foreground">
              Connected {new Date(status.connectedAt).toLocaleDateString()}
            </span>
          ) : null}
          {status.lastRotatedAt ? (
            <span className="text-xs text-muted-foreground">
              Last credential rotation {new Date(status.lastRotatedAt).toLocaleString()}
            </span>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => disconnectMutation.mutate(kitchenOrgId)}
          disabled={disconnectMutation.isPending}>
          {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
        </Button>
      </div>
      {disconnectMutation.error ? (
        <p className="mt-2 text-xs text-destructive">{disconnectMutation.error.message}</p>
      ) : null}
    </div>
  )
}

function ReauthorizationRequiredView({ lastRotatedAt }: { lastRotatedAt: string | null }) {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
      <Badge variant="outline" className="border-amber-400 bg-amber-100 text-xs text-amber-900">
        <AlertTriangle className="mr-1 h-3 w-3" />
        Re-authorization required
      </Badge>
      <p className="mt-2 text-sm text-amber-900">
        Tipalti's connection to Ordo has been invalidated. All AP payouts are paused. An Ordo admin will reach out to
        restore the connection; you don't need to take action.
      </p>
      {lastRotatedAt ? (
        <p className="mt-2 text-xs text-amber-900">Last rotation: {new Date(lastRotatedAt).toLocaleString()}</p>
      ) : null}
    </div>
  )
}

function StatusBadge({ status }: { status: TipaltiStatus | null }) {
  // FACTORY-476 — a manually-billed kitchen is not "not connected", it is fully
  // set up on a different rail. The fallthrough badge below is a nag.
  if (status === 'MANUAL_BILLING') {
    return (
      <Badge variant="default" className="ml-2 text-xs">
        Active
      </Badge>
    )
  }
  if (status === 'CONNECTED') {
    return (
      <Badge variant="default" className="ml-2 text-xs">
        Active
      </Badge>
    )
  }
  if (status === 'REAUTHORIZATION_REQUIRED') {
    return (
      <Badge variant="outline" className="ml-2 border-amber-400 bg-amber-100 text-xs text-amber-900">
        Re-auth needed
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="ml-2 text-xs">
      Not connected
    </Badge>
  )
}

function CardSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-16 w-full" />
    </div>
  )
}

function formatPayoutForChangeRequest(destination: KitchenPayoutDestination | null): string | null {
  if (!destination || destination.status === 'NOT_CONFIGURED') {
    return null
  }
  return `${destination.bankName ?? 'Unknown bank'} (•••• ${destination.last4 ?? '----'})`
}

/**
 * FACTORY-476 — the kitchen is paid outside Tipalti. No invite button and no
 * "not connected" framing: there is nothing for them to do, and the previous
 * fallthrough told them to onboard to a rail they will never be on.
 */
function ManualBillingView({ destination }: { destination: KitchenPayoutDestination | null }) {
  return (
    <div className="rounded-md border bg-card p-4">
      <p className="text-sm font-medium">Ordo invoices and pays this kitchen directly.</p>
      <p className="mt-1 text-sm text-muted-foreground">
        There is no Tipalti setup to complete. Each cycle Ordo emails an invoice for what you are owed and pays it by
        ACH or check on your kitchen&apos;s payout schedule.
      </p>
      {destination?.bankName ? (
        <p className="mt-2 text-xs text-muted-foreground">
          On file: {destination.bankName} •••• {destination.last4 ?? '----'}
        </p>
      ) : null}
      <p className="mt-2 text-xs text-muted-foreground">
        To change where invoices or payments go, use Request a payout change below.
      </p>
    </div>
  )
}
